import { WORD_STATE, buildMasterPool } from "../utils/words";
import { BadgesPanel } from "../components/shared/BadgesPanel";
import { ScorePill, MiniBars } from "../components/shared/ScorePill";
import { DailyMessage } from "../components/child/DailyMessage";

import iconSpelling from "../assets/icons/icon-spelling.webp";
import iconMeanings from "../assets/icons/icon-meanings.webp";
import iconSynonyms from "../assets/icons/icon-synonyms.webp";
import iconAntonyms from "../assets/icons/icon-antonyms.webp";

const ICON_STYLE = { width: 32, height: 32, borderRadius: 8, objectFit: "cover", marginBottom: ".4rem" };

export function ModernHomeLayout({
  user, data,
  onStartTest, onStartMeaningTest, onStartSynonymTest, onStartAntonymTest,
  onDataChange, settings, onShowProfile,
}) {
  const masterPool = buildMasterPool(data.wordLists);
  const mySessions = data.sessions.filter(s => s.userId === user.id);
  const mySpellingSessions = mySessions.filter(s => s.type !== "meaning" && s.type !== "synonym" && s.type !== "antonym");
  const myMeaningSessions  = mySessions.filter(s => s.type === "meaning");
  const mySynonymSessions  = mySessions.filter(s => s.type === "synonym");
  const myAntonymSessions  = mySessions.filter(s => s.type === "antonym");
  const streak = data.streaks?.[user.id] || 0;
  const today  = new Date().toDateString();
  const doneSpellingToday = mySpellingSessions.some(s => new Date(s.date).toDateString() === today);
  const doneMeaningToday  = myMeaningSessions.some(s => new Date(s.date).toDateString() === today);
  const doneSynonymToday  = mySynonymSessions.some(s => new Date(s.date).toDateString() === today);
  const doneAntonymToday  = myAntonymSessions.some(s => new Date(s.date).toDateString() === today);
  const avgScore = mySpellingSessions.length
    ? Math.round(mySpellingSessions.reduce((a, b) => a + b.score, 0) / mySpellingSessions.length) : 0;
  const myProgress        = Object.entries(data.progress || {}).filter(([k]) => k.startsWith(user.id) && !k.includes("_meaning_"));
  const myMeaningProgress = Object.entries(data.meaningProgress || {}).filter(([k]) => k.startsWith(user.id));
  const mastered          = myProgress.filter(([, v]) => v.mastered).length;
  const confident         = myProgress.filter(([, v]) => v.state === WORD_STATE.CONFIDENT).length;
  const poolSize          = masterPool.length;
  const attempted         = myProgress.filter(([, v]) => v.attempts > 0).length;
  const meaningAttempted  = myMeaningProgress.filter(([, v]) => v.attempts > 0).length;
  const meaningMastered   = myMeaningProgress.filter(([, v]) => v.mastered).length;
  const meaningConfident  = myMeaningProgress.filter(([, v]) => v.state === WORD_STATE.CONFIDENT).length;
  const myPoints  = data.points?.[user.id] || 0;
  const myGoalId  = data.goals?.[user.id] || null;
  const myGoal    = myGoalId ? (data.rewards || []).find(r => r.id === myGoalId) : null;

  return (
    <>
      <DailyMessage userName={user.name} streak={streak} />

      {myGoal && (
        <div style={{ background: "var(--blue-light)", border: "1.5px solid var(--blue)", borderRadius: 14, padding: ".75rem 1rem", marginBottom: "1.1rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: ".4rem" }}>
            <div style={{ fontWeight: 800, fontSize: ".85rem", color: "var(--blue)" }}>🎯 Goal: {myGoal.emoji} {myGoal.name}</div>
            <div style={{ fontSize: ".78rem", fontWeight: 700, color: "var(--blue)" }}>⭐ {myPoints} / {myGoal.cost}</div>
          </div>
          <div style={{ background: "rgba(0,0,0,.1)", borderRadius: 999, height: 8, overflow: "hidden" }}>
            <div style={{ height: "100%", background: myPoints >= myGoal.cost ? "var(--green)" : "var(--blue)", borderRadius: 999, width: `${Math.min(100, Math.round((myPoints / myGoal.cost) * 100))}%`, transition: "width .5s" }} />
          </div>
          {myPoints >= myGoal.cost && <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--green)", marginTop: ".35rem" }}>🎉 You've got enough points — go to Rewards to request it!</div>}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1.25rem" }}>
        <div className="hero-card" style={{ margin: 0 }}>
          <img src={iconSpelling} alt="" style={ICON_STYLE} />
          <div className="hero-lbl">Spelling Test</div>
          <div className="hero-title" style={{ fontSize: "1.1rem" }}>{poolSize > 0 ? `${poolSize} words` : "No words yet"}</div>
          <div className="hero-meta" style={{ fontSize: ".78rem" }}>20 words per session</div>
          <button className="hero-btn" style={{ padding: ".65rem 1.1rem", fontSize: ".85rem", marginTop: ".75rem" }} onClick={onStartTest} disabled={poolSize === 0 || doneSpellingToday}>
            {doneSpellingToday ? "✅ Done!" : "▶ Start"}
          </button>
        </div>
        <div className="hero-card" style={{ margin: 0, background: "linear-gradient(135deg,#14532d,#16a34a)" }}>
          <img src={iconMeanings} alt="" style={ICON_STYLE} />
          <div className="hero-lbl">Meaning Quiz</div>
          <div className="hero-title" style={{ fontSize: "1.1rem" }}>{poolSize > 0 ? `${poolSize} words` : "No words yet"}</div>
          <div className="hero-meta" style={{ fontSize: ".78rem" }}>10 questions per session</div>
          <button className="hero-btn" style={{ padding: ".65rem 1.1rem", fontSize: ".85rem", marginTop: ".75rem" }} onClick={onStartMeaningTest} disabled={poolSize === 0 || doneMeaningToday}>
            {doneMeaningToday ? "✅ Done!" : "📖 Start"}
          </button>
        </div>
        <div className="hero-card" style={{ margin: 0, background: "linear-gradient(135deg,#1e3a8a,#3b82f6)" }}>
          <img src={iconSynonyms} alt="" style={ICON_STYLE} />
          <div className="hero-lbl">Synonyms Quiz</div>
          <div className="hero-title" style={{ fontSize: "1.1rem" }}>{poolSize > 0 ? `${poolSize} words` : "No words yet"}</div>
          <div className="hero-meta" style={{ fontSize: ".78rem" }}>Same meaning</div>
          <button className="hero-btn" style={{ padding: ".65rem 1.1rem", fontSize: ".85rem", marginTop: ".75rem" }} onClick={onStartSynonymTest} disabled={poolSize === 0 || doneSynonymToday}>
            {doneSynonymToday ? "✅ Done!" : "🔤 Start"}
          </button>
        </div>
        <div className="hero-card" style={{ margin: 0, background: "linear-gradient(135deg,#581c87,#9333ea)" }}>
          <img src={iconAntonyms} alt="" style={ICON_STYLE} />
          <div className="hero-lbl">Antonyms Quiz</div>
          <div className="hero-title" style={{ fontSize: "1.1rem" }}>{poolSize > 0 ? `${poolSize} words` : "No words yet"}</div>
          <div className="hero-meta" style={{ fontSize: ".78rem" }}>Opposite meaning</div>
          <button className="hero-btn" style={{ padding: ".65rem 1.1rem", fontSize: ".85rem", marginTop: ".75rem" }} onClick={onStartAntonymTest} disabled={poolSize === 0 || doneAntonymToday}>
            {doneAntonymToday ? "✅ Done!" : "🔄 Start"}
          </button>
        </div>
      </div>

      {poolSize > 0 && (
        <div className="card">
          <div className="card-title">✏️ Spelling Progress</div>
          <div className="card-sub" style={{ marginBottom: "1rem" }}>{attempted} of {poolSize} words attempted</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: ".65rem", textAlign: "center" }}>
            {[
              { label: "New",       val: poolSize - attempted,                                                color: "var(--blue)",  emoji: "🆕" },
              { label: "Learning",  val: myProgress.filter(([,v]) => v.state === WORD_STATE.LEARNING).length, color: "var(--amber)", emoji: "📖" },
              { label: "Confident", val: confident,                                                           color: "var(--green)", emoji: "💪" },
              { label: "Mastered",  val: mastered,                                                            color: "var(--navy)",  emoji: "🏆" },
            ].map(({ label, val, color, emoji }) => (
              <div key={label} style={{ background: "var(--surface2)", borderRadius: 12, padding: ".75rem .5rem" }}>
                <div style={{ fontSize: "1.3rem" }}>{emoji}</div>
                <div style={{ fontSize: "1.2rem", fontWeight: 900, color }}>{val}</div>
                <div style={{ fontSize: ".68rem", color: "var(--muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {poolSize > 0 && (
        <div className="card">
          <div className="card-title">📖 Meaning Progress</div>
          <div className="card-sub" style={{ marginBottom: "1rem" }}>{meaningAttempted} of {poolSize} words attempted</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: ".65rem", textAlign: "center" }}>
            {[
              { label: "New",       val: poolSize - meaningAttempted,                                              color: "var(--blue)",  emoji: "🆕" },
              { label: "Learning",  val: myMeaningProgress.filter(([,v]) => v.state === WORD_STATE.LEARNING).length, color: "var(--amber)", emoji: "📖" },
              { label: "Confident", val: meaningConfident,                                                          color: "var(--green)", emoji: "💪" },
              { label: "Mastered",  val: meaningMastered,                                                           color: "var(--navy)",  emoji: "🏆" },
            ].map(({ label, val, color, emoji }) => (
              <div key={label} style={{ background: "var(--surface2)", borderRadius: 12, padding: ".75rem .5rem" }}>
                <div style={{ fontSize: "1.3rem" }}>{emoji}</div>
                <div style={{ fontSize: "1.2rem", fontWeight: 900, color }}>{val}</div>
                <div style={{ fontSize: ".68rem", color: "var(--muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-num" style={{ color: "var(--amber)" }}>🔥 {streak}</div>
          <div className="stat-lbl">Day Streak</div>
          <div className="stat-tip">Days in a row</div>
        </div>
        <div className="stat-card">
          <div className="stat-num">{avgScore || "—"}{avgScore ? "%" : ""}</div>
          <div className="stat-lbl">Avg Score</div>
        </div>
        <div className="stat-card">
          <div className="stat-num" style={{ color: "var(--amber)" }}>⭐ {myPoints}</div>
          <div className="stat-lbl">Points</div>
          <div className="stat-tip">Spend in Rewards</div>
        </div>
      </div>

      <BadgesPanel userId={user.id} data={data} />

      {mySpellingSessions.length > 0 ? (
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <div>
              <div className="card-title">Recent Spelling Results</div>
              <div className="card-sub">Last {Math.min(mySpellingSessions.length, 5)} tests</div>
            </div>
            <MiniBars sessions={mySpellingSessions.slice(-7)} />
          </div>
          <div className="session-row hdr"><span>Date</span><span>Words</span><span>Score</span><span>Time</span></div>
          {mySpellingSessions.slice(-5).reverse().map((s, i) => (
            <div key={i} className="session-row">
              <span style={{ color: "var(--muted)", fontSize: ".82rem" }}>{new Date(s.date).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })}</span>
              <span>{s.total}</span><ScorePill pct={s.score} />
              <span style={{ color: "var(--muted)" }}>{Math.floor(s.timeSec / 60)}m {s.timeSec % 60}s</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <div style={{ fontSize: "2.5rem", marginBottom: ".75rem" }}>✏️</div>
          <p>No tests yet — start your first spelling test above!</p>
        </div>
      )}

      {onShowProfile && (
        <div style={{ textAlign: "center", marginTop: ".5rem" }}>
          <button className="wm-btn secondary" onClick={onShowProfile}>⚙️ My Profile & Settings</button>
        </div>
      )}
    </>
  );
}
